import { fetchUtils, HttpError } from 'react-admin';
import {
  createReactAdminValidationError,
  getJsonApiErrorBody,
  getJsonApiErrorMessage,
  getJsonApiErrorStatus,
  JsonApiResource,
} from './JsonApiTypes';
import type { AuthProvider } from './AuthProvider';
import getMany from './getMany';
import { get, has } from 'lodash';
import batch, { BatchOperation } from './batch';

export interface CreateManyParams<T extends Omit<JsonApiResource, 'id'>> {
  data: T[];
  meta?: unknown;
}

export interface CreateManyResult<T extends JsonApiResource> {
  data: T[];
  meta?: unknown;
}

export type CreateMany = <T extends JsonApiResource>(
  resource: string,
  params: CreateManyParams<Omit<T, 'id'>>,
) => Promise<CreateManyResult<T>>;

export default function createMany(
  apiUrl: string,
  httpClient = fetchUtils.fetchJson,
  canAccess: typeof AuthProvider.canAccess = async () => true,
): CreateMany {
  const executeBatch = batch(apiUrl, httpClient, canAccess);

  return async <T extends JsonApiResource>(
    resource: string,
    params: CreateManyParams<Omit<T, 'id'>>,
  ): Promise<CreateManyResult<T>> => {
    try {
      const ops: BatchOperation[] = params.data.map((record) => ({
        op: 'add',
        data: record,
      }));

      if (ops.length === 0) {
        return { data: [] };
      }

      const result = await executeBatch(ops);

      if (!has(result, 'atomic:results')) {
        throw new HttpError('Invalid response from server', 500, { apiResponse: result });
      }

      const ids: string[] = result['atomic:results']
        .filter((record: unknown) => has(record, 'data.id'))
        .map((record) => String(get(record, 'data.id')));

      const records = await getMany(apiUrl, httpClient)(resource, { ids });

      return {
        data: records.data as (JsonApiResource & T)[],
        meta: result.meta,
      };
    } catch (error) {
      const validationErrors = createReactAdminValidationError(error as HttpError);
      throw new HttpError(
        getJsonApiErrorMessage(error as HttpError, 'Error creating records'),
        getJsonApiErrorStatus(error as HttpError),
        {
          ...validationErrors,
          apiResponse: getJsonApiErrorBody(error),
        },
      );
    }
  };
}
